import { isNil, omitBy, pick } from "lodash-es";

import { ModelMapper } from "@api/mappers/mappers.types";

import {
    DeliveryTypes,
    ProductVisibilityTypes,
    PromoPackTypes,
    SalesIncentiveType,
} from "@utils/enums";
import { getMedias } from "@utils/medias";
import { productConfigurations } from "@utils/productConfiguration/productConfiguration.utils";

import {
    CartApiModel,
    CartItemApiModel,
    CartPromotionApiModel,
} from "lib/@types/service/cart/cart.api.model";

import { translateWithoutContext } from "../../../value/translations/translations.helpers";
import {
    CartItemModel,
    CartModel,
    CartPromotionModel,
} from "./@types/cart.model";
import { BoxConditionModel } from "./cart.types";

const getTitle = (model: CartItemApiModel) => {
    if (model.articleConfiguration) {
        return model.articleConfiguration.title;
    }

    if (model.warrantyDeposit) {
        return translateWithoutContext("general.product.pallet.title");
    }

    return model.product?.productTitle || "";
};

const isAdditionalServiceItem = (
    id: string | null,
    items: CartItemApiModel[],
) => {
    return (
        !!id &&
        items.some(i => i.additionalServices?.map(as => as.id).includes(id))
    );
};

const CartItemMapper =
    (items: CartItemApiModel[]): ModelMapper<CartItemApiModel, CartItemModel> =>
    model => {
        return {
            deliveryType: model.deliveryType as DeliveryTypes,
            sku: model.sku,
            ean: model.ean,
            title: getTitle(model),
            medias: getMedias(
                model.warrantyDeposit,
                getTitle(model),
                model.product?.mainImages,
                model.articleConfiguration,
            ),
            id: model.id,
            warrantyDeposit: model.warrantyDeposit,
            articleConfiguration: model.articleConfiguration,
            giftCardArticle: !!model.product?.giftCardArticle,
            url: model.product?.url,
            quantity: model.quantity || 0,
            showStrippedPrice:
                !!model.articleStrikethroughPrice &&
                model.articleSalesPrice !== model.articleStrikethroughPrice &&
                !model.free,
            freeEntryAddedByCustomer: model.freeEntryAddedByCustomer,
            free: model.free,
            strikethroughPrice: model.articleStrikethroughPrice,
            salesPrice: model.articleSalesPrice,
            messages: model.messages || [],
            giftCardPersonalMessage: model.giftCardPersonalMessage,
            minimumOrderQuantity: model.product?.minimumOrderQuantity,
            roundingValue: model.product?.roundingValue,
            boxArticle: model.boxArticle,
            boxLogisticInformation: model.product?.boxLogisticInformation,
            totalPrice: model.totalPrice,
            deliveryTypes:
                model.product?.deliveryTypes.map(i => i as DeliveryTypes) || [],
            productVisibilityType: model.product
                ?.productVisibilityType as ProductVisibilityTypes,
            deliveryPromise: model.deliveryPromise,
            promotions: model.promotions.map(CartPromotionMapper),
            analytics: model.product?.analytics,
            brandName: model.product?.brandName,
            recupel: model.product?.recupel,
            hasProduct: !!model.product,
            additionalServices: model.additionalServices || [],
            references: {
                montages: model.product?.references?.montages,
            },
            isAdditionalService: isAdditionalServiceItem(model.id, items),
            configuration: omitBy(
                pick(
                    model,
                    productConfigurations.reduce(
                        (acc, pc) => [...acc, ...pc.configPaths],
                        [] as string[],
                    ),
                ),
                isNil,
            ),
            product: model.product!,
            additionalServiceAllowed: model.additionalServiceAllowed,
        };
    };

const CartPromotionMapper: ModelMapper<
    CartPromotionApiModel,
    CartPromotionModel
> = model => {
    return {
        commercialText: model.commercialText,
        promotionType: model.promotionType as PromoPackTypes,
        id: model.id,
    };
};

const getActiveBoxConditions = (cart: CartApiModel) => {
    if (!cart.conditions) {
        return [];
    }

    const conditions = cart.conditions;

    return (Object.keys(conditions) as Array<keyof typeof conditions>).reduce<
        Array<BoxConditionModel>
    >((arr, key) => {
        if (conditions![key].enabled) {
            arr.push({
                name: key,
                ...cart.conditions![key],
            });
        }
        return arr;
    }, []);
};

const getServiceTotal = (cart: CartApiModel) => {
    const additionalServiceCost = cart.items
        .filter(i => isAdditionalServiceItem(i.id, cart.items))
        .reduce((acc, curr) => {
            acc += curr.totalPrice;
            return acc;
        }, 0);

    return additionalServiceCost > 0 ? additionalServiceCost : null;
};

export const CartMapper: ModelMapper<CartApiModel, CartModel> = model => {
    return {
        baseStore: model.baseStore,
        customerId: model.customerId,
        bonusCardNumber: model.bonusCardNumber,
        storeId: model.shopId,
        deliveryCost: model.deliveryCost,
        subTotal: model.subTotalPrice,
        totalPrice: model.totalPrice,
        serviceTotal: getServiceTotal(model),
        cartsMerged: model.cartsMerged,
        items: model.items.map(i => CartItemMapper(model.items)(i)),
        salesIncentives: model.salesIncentives.map(
            i => i as SalesIncentiveType,
        ),
        callToActions: model.callToActions,
        promotions: model.promotions.map(CartPromotionMapper),
        giftCardTotal:
            model.giftCardPayments.length > 0 ? model.paidByGiftCards : null,
        discountTotal:
            (model.globalDiscounts || 0) > 0 ? model.globalDiscounts : null,
        messages: model.messages,
        checkoutStep: model.checkoutStep,
        activeBoxConditions: getActiveBoxConditions(model),
        promotionCodes: model.promotionCodes,
        giftCardPayments: model.giftCardPayments,
        billingAddressId: model.billingAddressId,
        deliveryAddressId: model.deliveryAddressId,
        globalDiscounts: model.globalDiscounts,
        acceptedAdditionalServiceDocumentCodes:
            model.acceptedAdditionalServiceDocumentCodes ?? [],
    };
};
